/** East Africa Time (Nairobi) helpers — EAT is UTC+3, no daylight saving. */

export type MealPeriod = 'breakfast' | 'lunch' | 'dinner' | 'latenight';

const EAT_OFFSET_MS = 3 * 60 * 60 * 1000;

/** Current wall-clock time in Nairobi (EAT). */
export function getNairobiTime(date: Date = new Date()): { hours: number; minutes: number } {
  const utcMs = date.getTime() + date.getTimezoneOffset() * 60_000;
  const eat = new Date(utcMs + EAT_OFFSET_MS);
  return { hours: eat.getHours(), minutes: eat.getMinutes() };
}

/** Which meal period it is right now in Kenya. */
export function getMealPeriod(date: Date = new Date()): MealPeriod {
  const { hours } = getNairobiTime(date);
  if (hours >= 5 && hours < 11) return 'breakfast';
  if (hours >= 11 && hours < 16) return 'lunch';
  if (hours >= 16 && hours < 22) return 'dinner';
  return 'latenight';
}

export interface PeriodInfo {
  period: MealPeriod;
  /** Swahili greeting */
  greeting: string;
  /** English greeting */
  greetingEn: string;
  /** Short label, e.g. "Breakfast time in Nairobi" */
  headline: string;
  /** Menu tab to highlight */
  menuCategory: 'breakfast' | 'lunch' | 'dinner';
  /** Friendly description of what's cooking */
  blurb: string;
}

export function getPeriodInfo(date: Date = new Date()): PeriodInfo {
  const period = getMealPeriod(date);
  switch (period) {
    case 'breakfast':
      return {
        period,
        greeting: 'Habari ya asubuhi',
        greetingEn: 'Good morning',
        headline: "It's breakfast o'clock in Kenya",
        menuCategory: 'breakfast',
        blurb: 'Start your day the Kenyan way — hot chai, fresh mandazi and hearty morning plates.',
      };
    case 'lunch':
      return {
        period,
        greeting: 'Habari ya mchana',
        greetingEn: 'Good afternoon',
        headline: "It's lunch time in Kenya",
        menuCategory: 'lunch',
        blurb: 'Mchana specials are on the jiko — ugali, pilau, githeri and more, cooked fresh.',
      };
    case 'dinner':
      return {
        period,
        greeting: 'Habari ya jioni',
        greetingEn: 'Good evening',
        headline: "It's dinner time in Kenya",
        menuCategory: 'dinner',
        blurb: 'The jiko is roaring — nyama choma, kienyeji chicken and slow-cooked stews tonight.',
      };
    case 'latenight':
      return {
        period,
        greeting: 'Karibu MLO',
        greetingEn: 'Late night',
        headline: 'Late-night cravings? Sawa.',
        menuCategory: 'dinner',
        blurb: 'The kitchen never sleeps — order late-night bites and we will sort you out.',
      };
  }
}

/** Formatted current time in Nairobi, e.g. "2:35 PM". */
export function formatNairobiTime(date: Date = new Date()): string {
  const { hours, minutes } = getNairobiTime(date);
  const h12 = hours % 12 === 0 ? 12 : hours % 12;
  const ampm = hours < 12 ? 'AM' : 'PM';
  return `${h12}:${minutes.toString().padStart(2, '0')} ${ampm}`;
}

/** Estimated delivery time range (EAT), e.g. "2:35 – 2:50 PM EAT". */
export function getDeliveryEstimate(minMinutes = 30, maxMinutes = 45): string {
  const now = Date.now();
  const fmt = (d: Date) => formatNairobiTime(d);
  return `${fmt(new Date(now + minMinutes * 60_000))} – ${fmt(new Date(now + maxMinutes * 60_000))}`;
}
